import React, { useRef, useState, useEffect } from 'react';
import { THEME } from '../../styles/theme';
import { describeVoiceError } from '../../services/voiceService';

const QUICK_PROMPTS = [
  { label: "Today's bias?",    text: "What is the XAU/USD bias for today? D1 and H4 analysis please." },
  { label: 'Current setup?',   text: 'Analyze the current XAU/USD setup. Any A/A+ grade entries available?' },
  { label: 'London plan?',     text: 'What should I watch during London session today? Key levels and setups.' },
  { label: 'Rate psychology',  text: 'Ask me a quick psychological check before I trade today.' },
  { label: 'Weekly review',    text: 'Give me a brief weekly summary of what has been working and what needs improvement.' },
];

const S = {
  wrap: {
    borderTop:      `1px solid rgba(0,212,255,0.12)`,
    background:     'rgba(8,12,24,0.97)',
    backdropFilter: 'blur(8px)',
    flexShrink:     0,
  },
  quickRow: {
    display:    'flex',
    gap:        '8px',
    padding:    '10px 16px 0',
    overflowX:  'auto',
    scrollbarWidth: 'none',
  },
  quickBtn: {
    padding:    '5px 12px',
    borderRadius: '20px',
    border:     `1px solid rgba(0,212,255,0.2)`,
    background: 'rgba(0,212,255,0.06)',
    color:      THEME.colors.textDim,
    fontSize:   '12px',
    cursor:     'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all 0.15s',
  },
  inputRow: {
    display:    'flex',
    alignItems: 'flex-end',
    gap:        '10px',
    padding:    '10px 16px 14px',
  },
  imgPreview: {
    position:   'relative',
    flexShrink: 0,
  },
  previewImg: {
    width:        '48px',
    height:       '48px',
    borderRadius: '6px',
    objectFit:    'cover',
    border:       `1px solid rgba(255,215,0,0.4)`,
  },
  removeImg: {
    position:   'absolute',
    top:        '-6px',
    right:      '-6px',
    width:      '18px',
    height:     '18px',
    borderRadius: '50%',
    background: THEME.colors.red,
    color:      '#fff',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '11px',
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textarea: {
    flex:         1,
    background:   'rgba(255,255,255,0.05)',
    border:       `1px solid rgba(0,212,255,0.2)`,
    borderRadius: '12px',
    color:        THEME.colors.text,
    fontSize:     '15px',
    padding:      '10px 14px',
    resize:       'none',
    outline:      'none',
    fontFamily:   THEME.fonts.body,
    lineHeight:   '1.5',
    minHeight:    '44px',
    maxHeight:    '140px',
    overflowY:    'auto',
    transition:   'border-color 0.2s',
  },
  iconBtn: (disabled) => ({
    width:          '44px',
    height:         '44px',
    borderRadius:   '12px',
    border:         'none',
    background:     disabled ? 'rgba(255,255,255,0.05)' : 'rgba(0,212,255,0.12)',
    color:          disabled ? 'rgba(255,255,255,0.2)' : THEME.colors.blue,
    fontSize:       '18px',
    cursor:         disabled ? 'not-allowed' : 'pointer',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0,
    transition:     'all 0.15s',
  }),
  sendBtn: (disabled) => ({
    width:          '44px',
    height:         '44px',
    borderRadius:   '12px',
    border:         'none',
    background:     disabled ? 'rgba(255,255,255,0.05)' : THEME.colors.blue,
    color:          disabled ? 'rgba(255,255,255,0.2)' : '#000',
    fontSize:       '18px',
    cursor:         disabled ? 'not-allowed' : 'pointer',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0,
    transition:     'all 0.15s',
    fontWeight:     '700',
  }),
};

export default function ChatInput({ onSend, isThinking }) {
  const [text, setText]             = useState('');
  const [screenshot, setScreenshot] = useState(null);   // base64 string
  const [previewUrl, setPreviewUrl] = useState(null);   // object URL for preview
  const [isListening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const textareaRef = useRef(null);
  const fileRef     = useRef(null);
  const recognizer  = useRef(null);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }, [text]);

  const canSend = (text.trim() || screenshot) && !isThinking;

  const handleSend = () => {
    if (!canSend) return;
    onSend(text.trim(), screenshot);
    setText('');
    setScreenshot(null);
    setPreviewUrl(null);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuick = (prompt) => {
    setText(prompt);
    textareaRef.current?.focus();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const full   = ev.target.result;          // data:image/jpeg;base64,XXXX
      const base64 = full.split(',')[1];         // strip prefix
      setScreenshot(base64);
      setPreviewUrl(full);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceError('Ha browser voice input support करत नाही. Chrome किंवा Edge वापरा.'); return; }

    if (isListening) {
      recognizer.current?.stop();
      setListening(false);
      return;
    }

    setVoiceError('');
    const r = new SR();
    r.lang         = 'en-IN';
    r.continuous   = false;
    r.interimResults = false;
    r.onresult  = (e) => {
      const spoken = e.results[0][0].transcript;
      setText(prev => prev ? prev + ' ' + spoken : spoken);
    };
    // Previously silent — no onerror handler at all, so a denied mic
    // permission or missing microphone just quietly stopped "listening"
    // with zero indication of why. Now surfaces an actual reason.
    r.onerror = (e) => {
      setListening(false);
      const msg = describeVoiceError(e.error);
      if (msg) setVoiceError(msg);
    };
    r.onend = () => setListening(false);
    r.start();
    recognizer.current = r;
    setListening(true);
  };

  return (
    <div style={S.wrap}>
      {/* Voice error — was previously silent (no onerror handler at all) */}
      {voiceError && (
        <div style={{
          margin: '8px 16px 0', padding: '8px 12px', borderRadius: '8px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#f87171', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
        }}>
          <span>🎙️ {voiceError}</span>
          <button onClick={() => setVoiceError('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
        </div>
      )}

      {/* Quick-prompt chips */}
      <div style={S.quickRow}>
        {QUICK_PROMPTS.map(q => (
          <button
            key={q.label}
            style={S.quickBtn}
            onClick={() => handleQuick(q.text)}
            disabled={isThinking}
          >
            {q.label}
          </button>
        ))}
      </div>

      {/* Input row */}
      <div style={S.inputRow}>
        {/* Screenshot preview */}
        {previewUrl && (
          <div style={S.imgPreview}>
            <img src={previewUrl} alt="chart" style={S.previewImg} />
            <button style={S.removeImg} onClick={() => { setScreenshot(null); setPreviewUrl(null); }}>✕</button>
          </div>
        )}

        {/* Attach screenshot */}
        <button style={S.iconBtn(isThinking)} onClick={() => fileRef.current?.click()} title="Attach chart screenshot" disabled={isThinking}>
          📎
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />

        {/* Text input */}
        <textarea
          ref={textareaRef}
          style={S.textarea}
          placeholder={isThinking ? 'JARVIS is thinking...' : 'Ask JARVIS about XAU/USD...  (Enter to send, Shift+Enter for new line)'}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          disabled={isThinking}
          rows={1}
        />

        {/* Voice */}
        <button
          style={{ ...S.iconBtn(isThinking), background: isListening ? 'rgba(255,51,51,0.2)' : undefined, color: isListening ? THEME.colors.red : undefined }}
          onClick={handleVoice}
          title={isListening ? 'Stop recording' : 'Voice input'}
          disabled={isThinking}
        >
          {isListening ? '⏹' : '🎙'}
        </button>

        {/* Send */}
        <button style={S.sendBtn(!canSend)} onClick={handleSend} disabled={!canSend} title="Send (Enter)">
          ➤
        </button>
      </div>
    </div>
  );
}
